/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    chartZAxisColor,
    chartTableColors
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './emailssentreport_styles';



/* ========== ~~~~~~~~~~ COMMENTING ANALYTICS - COMMENTING EMAILS SENT ~~~~~~~~~~ ========== */
const EmailsSentReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.commenting.emailsSent);
    const { filters } = useSelector((state) => state.app);
    const changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const products = [
        { id: 'commentingNotificationsSent', name: 'Commenting Notification Sent', color: chartTableColors[0] },
        { id: 'returningUsersCount', name: 'Returning Users Count', color: chartZAxisColor }
    ];
    const dates = details ? details[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if (status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'commenting');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        setChartLegends(productArr);
    };

    const getBarChartKeys = () => products.map(product => product.name).reverse();

    const getBarChartColors = () => products.map(product => product.color).reverse();

    const renderBarChartLegends = () => {
        return chartLegends.map((legend, index) =>
        (
            <ChartLegend
                title={legend.name}
                checkboxColor={legend.color}
                checked={legend.checked}
                onCheck={checked => {
                    const legends = [...chartLegends];
                    legends[index] = {
                        ...legends[index],
                        checked: checked
                    }

                    setChartLegends(legends);
                }}
                value={formatNumber(legend.value)}
                changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                changeType={changeType}
                metricType={'commenting'}
            />
        )
        )
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(productData => productData.checked === true);
                if (checkedProducts.map(productData => productData.id).indexOf(product.id) >= 0) {
                    if (product.id === 'returningUsersCount')
                        item['z'] = detailsDateData[product.id];
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if (productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const emailsSentBarChart = {
        indexBy: 'date',
        data: transformBarChartData(),
        keys: getBarChartKeys(),
        colors: getBarChartColors(),
        legends: renderBarChartLegends()
    }

    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Emails Sent'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.commentingNotificationsSentRateChange, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.commentingNotificationsSentRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.commentingNotificationsSent) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={emailsSentBarChart.colors}
            keys={emailsSentBarChart.keys}
            data={emailsSentBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zAxisId={'returningUsersCount'}
            zAxisColor={chartZAxisColor}
        />
    );

  const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'commenting'}
            products={products}
            data={details ? details[selectedInterval] : null}
            csvFileName={'Emails Sent Report'}
        />
    ) : []

    const isNoData = () => {
        return (status === 'rejected') ||
            (status === 'fulfilled' && !overview?.period?.commentingNotificationsSent) ||
            (status === 'fulfilled' && Object.keys(overview).length === 0);
    }

    return (
        <div name={'emailssentreport'} class={`${props.classes.emailsSentReport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Emails Sent'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Emails Sent</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                Users are able to enable notifications where we will send them emails to let them know when
                                someone has responded to their comment. Within the email, we allow that commenter to click on
                                a link that will take them directly back to the site and commenting unit which is the Returning Users Count metric.
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                // loading={status === 'pending' ? true : false}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={emailsSentBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default EmailsSentReport;
